// Read-only rendering of the same No-Dues content the employee filled in on
// their own No-Dues form (NoDuesForm.tsx) — same labels, same field order,
// same values. Reused by the HR screens (HRVerificationDetail.tsx,
// NoDuesHRReviewDetail.tsx) and the department/approver review screens
// (NoDuesDeptAction.tsx, NoDuesApproverReview.tsx) so every reviewer sees the
// identical No-Dues form the employee submitted.
//
// Scope: the genuinely No-Dues-specific content the employee actually
// typed/submitted — Controlling & Reviewing Officers, the fields they filled
// in (mobile/wing/notice period/bond/leave), the outstanding-loans snapshot
// captured at submission, and the surety/declaration block. NoDuesForm.tsx's
// own Section 03 (Separation Reference) is NOT repeated here — every
// consuming screen already shows that resignation data elsewhere on the same
// page. Section 01 (Employee Details) IS optionally included — pass
// `employee` only on a screen that doesn't already render the SAP master
// grid elsewhere (currently: NoDuesDispatch.tsx); leave it out on screens
// that already show it (ResignationFormSections, or their own hand-rolled
// Employee Details block) to avoid duplicating it.
//
// `form` is the No-Dues submission payload exactly as NoDuesForm.tsx built it
// (see its handleSubmit): { mobile, wing, separation_mode,
// notice_period_served, bond_amount, leave_current_month, training_provided,
// training_details, other_bond_submitted, other_bond_details,
// controlling_officer: {name,emp_no,designation},
// reviewing_authorities: [{role,name,emp_no,designation}],
// outstanding_loans, advances_availed, loan_surety_flag, loan_surety_details,
// advance_sureties: [{advance_type,first_surety_emp_id,first_surety_emp_name,
// second_surety_emp_id,second_surety_emp_name}], declaration_accepted }.
import React from "react";
import { OutstandingLoansSection } from "./components";
import { formatDate } from "../services/eSeparation.service";

// Mirrors NoDuesForm.tsx's own NO_DUES_ADVANCE_TYPES exactly — the fixed
// 4-type surety table the employee fills in.
const NO_DUES_ADVANCE_TYPES: { code: string; label: string }[] = [
  { code: "HBA", label: "HBA Advance" },
  { code: "VEHICLE", label: "Vehicle Advance" },
  { code: "MULTIPURPOSE", label: "Multipurpose Advance" },
  { code: "DMRC_CREDIT_SOCIETY", label: "DMRC Credit Society" },
];

const fmt = (v: any): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return formatDate(v);
  return String(v);
};

const isYes = (v: any): boolean => v === "Y" || v === true || v === "true" || v === "Yes" || v === "yes";

const SectionShell: React.FC<{
  number: number;
  title: string;
  children: React.ReactNode;
}> = ({ number, title, children }) => (
  <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
    <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
      <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
        <span
          className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
          style={{ width: 34, height: 34, fontSize: 15 }}
        >
          {String(number).padStart(2, "0")}
        </span>
        {title}
      </h5>
    </div>
    <div className="card-body p-4">{children}</div>
  </div>
);

const ReadField: React.FC<{ label: string; value: any; colClass?: string }> = ({
  label,
  value,
  colClass = "col-lg-3 col-md-4 col-sm-6",
}) => (
  <div className={colClass}>
    <label className="form-label fw-semibold text-secondary small mb-1">{label}</label>
    <input type="text" className="form-control bg-light" value={fmt(value)} readOnly />
  </div>
);

export interface NoDuesFormSectionsProps {
  /** The submitted No-Dues form payload (`detail.form` / `ndDetail.form`, etc.) */
  form: any;
  /** Section number the first sub-section should render as (default 1). */
  startNumber?: number;
  /**
   * Some callers (department clearance review) already resolve a more
   * current/merged outstanding-loans list than the frozen submission-time
   * snapshot in `form.outstanding_loans` — pass it here to show that instead,
   * since it's load-bearing for the department's recovery workflow.
   */
  loansOverride?: any[];
  /**
   * The resignation/employee record (same shape getResignationDetails()
   * returns) — pass this only when the consuming screen does NOT already
   * show the SAP master-data grid elsewhere, to render NoDuesForm.tsx's own
   * "Employee Details (Auto-filled)" section here too.
   */
  employee?: any;
}

export const NoDuesFormSections: React.FC<NoDuesFormSectionsProps> = ({
  form,
  startNumber = 1,
  loansOverride,
  employee,
}) => {
  const f = form || {};
  const co = f.controlling_officer || {};
  const hasCo = !!(co.name || co.emp_no);
  const reviewingAuthorities: { role: string; name: any; emp_no: any; designation: any }[] =
    Array.isArray(f.reviewing_authorities) ? f.reviewing_authorities : [];

  let n = startNumber;
  const employeeDetailsNumber = employee ? n++ : -1;
  const officersNumber = n++;
  const detailsNumber = n++;
  const loansNumber = n++;
  const suretyNumber = n++;

  return (
    <>
      {/* Mirrors NoDuesForm.tsx SECTION 01: EMPLOYEE DETAILS (AUTO-FILLED) —
          only rendered when the caller doesn't already show this elsewhere. */}
      {employee && (
        <SectionShell number={employeeDetailsNumber} title="Employee Details (Auto-filled)">
          <div className="row g-3">
            <ReadField label="Name" value={employee.emp_name} />
            <ReadField label="Employee No." value={employee.emp_no} />
            <ReadField label="Designation" value={employee.designation_text || employee.designation} />
            <ReadField label="Wing" value={employee.wing} />
            <ReadField label="Department" value={employee.department_name || employee.posting} />
            <ReadField label="Pay Scale" value={employee.pay_scale} />
            <ReadField label="Basic Pay" value={employee.basic_pay} />
            <ReadField label="Date of Joining" value={employee.doj} />
            <ReadField
              label="Employee Group"
              value={
                employee.emp_group === "A"
                  ? "Executive"
                  : employee.emp_group === "B"
                  ? "Non-Executive"
                  : employee.emp_group
              }
            />
          </div>
        </SectionShell>
      )}

      {/* Mirrors NoDuesForm.tsx SECTION 02: CONTROLLING & REVIEWING OFFICERS */}
      {(hasCo || reviewingAuthorities.length > 0) && (
        <SectionShell number={officersNumber} title="Controlling & Reviewing Officers">
          {hasCo && (
            <div className="mb-4">
              <h6 className="fw-bold text-primary mb-3">Controlling Officer Details</h6>
              <div className="row g-3">
                <ReadField label="Name" value={co.name} colClass="col-lg-4 col-md-4 col-sm-6" />
                {co.emp_no && (
                  <ReadField label="Employee No." value={co.emp_no} colClass="col-lg-4 col-md-4 col-sm-6" />
                )}
                {co.designation && (
                  <ReadField label="Designation" value={co.designation} colClass="col-lg-4 col-md-4 col-sm-6" />
                )}
              </div>
            </div>
          )}

          {reviewingAuthorities.length > 0 && (
            <div>
              <h6 className="fw-bold text-primary mb-1">Reviewing / Reporting Officer Details</h6>
              <p className="text-muted small mb-3">
                For Non-Exe → Dy. HOD, for AM up to Dy. HOD → HOD, for HOD → Director.
              </p>
              <div className="d-flex flex-column gap-3">
                {reviewingAuthorities.map((a, i) => (
                  <div key={i} className="p-3 border rounded-3 bg-light">
                    <div className="badge bg-secondary mb-2 px-2 py-1">{a.role}</div>
                    <div className="row g-3">
                      <div className="col-lg-4 col-md-4 col-sm-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">Name</label>
                        <input type="text" className="form-control bg-white" value={fmt(a.name)} readOnly />
                      </div>
                      <div className="col-lg-4 col-md-4 col-sm-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">Employee No.</label>
                        <input type="text" className="form-control bg-white" value={fmt(a.emp_no)} readOnly />
                      </div>
                      {a.designation && (
                        <div className="col-lg-4 col-md-4 col-sm-6">
                          <label className="form-label fw-semibold text-secondary small mb-1">Designation</label>
                          <input type="text" className="form-control bg-white" value={fmt(a.designation)} readOnly />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SectionShell>
      )}

      {/* Mirrors NoDuesForm.tsx SECTION 04: DETAILS TO BE FILLED BY EMPLOYEE */}
      <SectionShell number={detailsNumber} title="Details Filled by Employee (No-Dues Form)">
        <div className="row g-3">
          <ReadField label="Mobile Number" value={f.mobile} colClass="col-lg-4 col-md-6 col-sm-12" />
          <ReadField label="Wing" value={f.wing} colClass="col-lg-4 col-md-6 col-sm-12" />
          <ReadField
            label="Notice Period Served"
            value={f.notice_period_served}
            colClass="col-lg-4 col-md-6 col-sm-12"
          />
          <ReadField label="Bond Amount, if any" value={f.bond_amount} colClass="col-lg-4 col-md-6 col-sm-12" />
          <ReadField
            label="Leave for current month received from department"
            value={f.leave_current_month}
            colClass="col-lg-8 col-md-6 col-sm-12"
          />
          <ReadField label="Mode of Separation" value={f.separation_mode} colClass="col-lg-4 col-md-6 col-sm-12" />
          <ReadField
            label="Whether any training provided by DMRC (including Induction Course or nay other course) (in India/Abroad)"
            value={isYes(f.training_provided) ? "Yes" : "No"}
            colClass="col-lg-6 col-md-6 col-sm-12"
          />
          {isYes(f.training_provided) && (
            <ReadField label="Training Details" value={f.training_details} colClass="col-lg-6 col-md-6 col-sm-12" />
          )}
          <ReadField
            label="Whether any other Bond submitted to DMRC other than the Joining Bond"
            value={isYes(f.other_bond_submitted) ? "Yes" : "No"}
            colClass="col-lg-6 col-md-6 col-sm-12"
          />
          {isYes(f.other_bond_submitted) && (
            <ReadField label="Other Bond Details" value={f.other_bond_details} colClass="col-lg-6 col-md-6 col-sm-12" />
          )}
        </div>
      </SectionShell>

      {/* Mirrors NoDuesForm.tsx SECTION 05: OUTSTANDING LOANS / ADVANCES
          (the snapshot captured at No-Dues submission time). */}
      <SectionShell number={loansNumber} title="Outstanding Loans / Advances (No-Dues Submission)">
        <OutstandingLoansSection
          loans={loansOverride ?? f.outstanding_loans ?? []}
          note="Auto-fetched from the employee's SAP loan records at the time the No-Dues form was submitted."
        />
        <div className="row g-3 mt-1 pt-3 border-top">
          <ReadField
            label="Advances availed from DMRC (HBA/Vehicle/Multipurpose/Laptop/DMRC Credit Society)"
            value={f.advances_availed}
            colClass="col-lg-6 col-md-8 col-sm-12"
          />
        </div>
      </SectionShell>

      {/* Mirrors NoDuesForm.tsx SECTION 06: SURETY & DECLARATION */}
      <SectionShell number={suretyNumber} title="Surety & Declaration">
        <div className="mb-4">
          <label className="form-label fw-semibold text-secondary small mb-1">
            Sureties for Loans (Auto-fetched)
          </label>
          <input
            type="text"
            className="form-control bg-light mb-1"
            value={f.loan_surety_flag === "Y" ? fmt(f.loan_surety_details || "Yes") : "No sureties on record"}
            readOnly
          />
        </div>

        <div className="mb-4">
          <label className="form-label fw-semibold text-secondary small mb-2">
            Advances availed from DMRC — Name &amp; Emp. No. of employees for whom Surety is given
          </label>
          <div className="table-responsive">
            <table className="table table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 48 }}>S.No.</th>
                  <th>Type of Advance</th>
                  <th>Name, E.No. of employee for whom Surety is given</th>
                  <th>Name, E.No. of employee for whom Surety is given</th>
                </tr>
              </thead>
              <tbody>
                {NO_DUES_ADVANCE_TYPES.map((t, i) => {
                  const row = Array.isArray(f.advance_sureties) ? f.advance_sureties[i] : null;
                  const firstLabel = row?.first_surety_emp_id
                    ? row.first_surety_emp_id === "N/A"
                      ? "N/A"
                      : `${row.first_surety_emp_name || ""} (${row.first_surety_emp_id})`.trim()
                    : "N/A";
                  const secondLabel = row?.second_surety_emp_id
                    ? row.second_surety_emp_id === "N/A"
                      ? "N/A"
                      : `${row.second_surety_emp_name || ""} (${row.second_surety_emp_id})`.trim()
                    : "N/A";
                  return (
                    <tr key={t.code}>
                      <td>{i + 1}</td>
                      <td className="fw-semibold">{t.label}</td>
                      <td>{firstLabel}</td>
                      <td>{secondLabel}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div
          className="p-3 rounded-3 border d-flex align-items-start gap-2"
          style={{
            background: f.declaration_accepted ? "#f1f6fb" : "#f8f9fa",
            borderColor: f.declaration_accepted ? "#0d6efd" : "#dee2e6",
          }}
        >
          <i
            className={`ti ${f.declaration_accepted ? "ti-circle-check text-primary" : "ti-circle text-muted"} mt-1 flex-shrink-0`}
          />
          <span className="text-dark" style={{ fontSize: 14 }}>
            {f.declaration_accepted
              ? "The employee declared that all items issued to them have been returned and nothing is outstanding against their name."
              : "Declaration not yet accepted by the employee."}
          </span>
        </div>
      </SectionShell>
    </>
  );
};
